<template>
  <div :class="{ 'w-full': stretch, 'add-to-cart--quote-mode': addToQuoteLayout }">
    <div class="flex w-full rounded-sm layout__container align-center">
      <CCounterNew
        :size="size == 'medium' ? 'large' : 'mini'"
        class="mr-1 add-to-cart-counter"
        :class="{ 'justify-end': !addToQuoteLayout }"
        data-test="add-to-cart-counter"
        :multiple="orderMultiple"
        :initial-value="quantity"
        :placeholder="salesPriceUnitLabel.toLocaleLowerCase() || t('piece')"
        :button-class="counterButtonClass"
        :hide-increment="hideIncrement"
        :stretch="stretch"
        @waiting="handleCounterWaiting"
        @count-change="(value:number) => onCountChange(value.toString())"
        @keydown="onKeyDown"
      >
        <CInputGroup
          v-if="hasSalesUnit"
          :class="{ 'min-w-[4rem] max-w-[6rem]': stretch, 'w-16': !stretch }"
          name="salesUnitValue"
          :placeholder="salesUnitLabel.toLocaleLowerCase() || t('square_meter')"
          type="text"
          pattern="[0-9]*"
          inputmode="numeric"
          autocomplete="off"
          :maxlength="7"
          :value="salesUnitValue.toString()"
          @keydown="onKeyDown"
          @update:model-value="(value:string) => handleInputSalesUnit(value)"
        />
      </CCounterNew>
      <div
        v-if="!hideBuyButton || addToQuoteLayout"
        class="flex items-center layout__button-layout"
        :class="{ 'pl-2': !addToQuoteLayout }"
      >
        <CButton
          v-if="addToQuoteLayout"
          variant="green"
          :shape="buttonShape"
          class="layout__buy-button"
          :class="{ 'mr-2': isAddToCartButtonVisible }"
          :size="size"
          :disabled="counterIsWaiting"
          data-test="add-to-quote-button"
          @click="onAddToQuoteClick(quantity)"
          >{{ quoteButtonLabel ?? t('select') }}</CButton
        >

        <CButton
          v-if="isAddToCartButtonVisible"
          :variant="addToQuoteLayout ? 'secondary' : 'green'"
          :shape="buttonShape"
          class="layout__buy-button"
          :size="size"
          :disabled="buyDisabled || counterIsWaiting"
          data-test="add-button"
          @click="onClick(quantity)"
          >{{ buttonLabel ?? t('cart.buy') }}</CButton
        >
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useI18n } from 'vue-i18n';
import type { CButtonShape, CButtonSize } from '@/types/ui';

const { t } = useI18n();

type Props = {
  size?: CButtonSize;
  stretch?: boolean;
  unitConversionFactor?: number;
  salesUnitLabel?: string;
  salesPriceUnitLabel?: string;
  buttonShape?: CButtonShape;
  buyDisabled?: boolean;
  orderMultiple?: number;
  initialQuantity?: number;
  hideIncrement?: boolean;
  buttonLabel?: string;
  quoteButtonLabel?: string;
  addToQuoteLayout?: 'extended' | 'simple';
};

const props = withDefaults(defineProps<Props>(), {
  size: 'medium',
  stretch: false,
  unitConversionFactor: 0,
  salesUnitLabel: '',
  salesPriceUnitLabel: '',
  buttonShape: 'squared',
  buyDisabled: false,
  orderMultiple: 1,
  initialQuantity: 1,
  hideIncrement: false,
  buttonLabel: undefined,
  quoteButtonLabel: undefined,
  addToQuoteLayout: undefined,
});

const emit = defineEmits<{
  (e: 'quantity-update', value: number): void;
  (e: 'click', value: number): void;
  (e: 'add-to-quote-click', value: number): void;
}>();

const hideBuyButton = inject('hideBuyButton', false);
const counterButtonClass = inject('counterButtonClass', '');

const counterIsWaiting = ref(false);
const quantity = ref(props.initialQuantity);
const salesUnitValue = ref('');

const hasSalesUnit = computed(() => props.unitConversionFactor > 0);

const isAddToCartButtonVisible = computed(() => {
  if (hideBuyButton || (props.addToQuoteLayout && props.addToQuoteLayout !== 'extended')) return false;

  return true;
});

const handleCounterWaiting = (value: boolean) => {
  counterIsWaiting.value = value;
};

const onCountChange = (value: string) => {
  counterIsWaiting.value = true;
  calculateQuantities('quantity', value);
};

const onKeyDown = (event: KeyboardEvent) => {
  handleKeyRestrictions(event, { allowedKeys: [',', '.'], allowLetter: false });
};

const onClick = (quantity: number) => {
  emit('click', quantity);
};

const onAddToQuoteClick = (quantity: number) => {
  emit('add-to-quote-click', quantity);
};

const calculateQuantities = (field: string, value: string) => {
  // prevents a user from typing more than one period
  const cleanValue = +value.replace(/,/g, '.').split('.').slice(0, 2).join('.');

  if (props.unitConversionFactor) {
    const { newQuantity, newSalesUnitValue } = calculateQuantityAndSalesUnit({
      field,
      quantity: field === 'quantity' ? cleanValue : quantity.value,
      salesUnitValue: field === 'salesUnitValue' ? cleanValue : salesUnitValue.value,
      unitConversionFactor: +props.unitConversionFactor,
      multiple: props.orderMultiple,
    });
    quantity.value = newQuantity;
    salesUnitValue.value = newSalesUnitValue;
    emit('quantity-update', newQuantity);
  } else {
    quantity.value = Number(value);
    emit('quantity-update', quantity.value);
  }

  counterIsWaiting.value = false;
};

const handleInputSalesUnit = (value: string) => {
  salesUnitValue.value = value;
  counterIsWaiting.value = true;
  debounceCalculateQuantities('salesUnitValue', value);
};

const debounceCalculateQuantities = debounce(function (field: string, value: string) {
  calculateQuantities(field, value);
  counterIsWaiting.value = false;
}, 800);

watch(
  () => props.initialQuantity,
  (newValue) => {
    quantity.value = newValue;
  },
);

onMounted(() => {
  calculateQuantities('quantity', `${props.initialQuantity}`);
});
</script>

<script lang="ts">
export default defineComponent({
  name: 'AddToCartInputGroup',
});
</script>

<style lang="scss" scoped>
.add-to-cart--PDP-configurator-mobile {
  .layout {
    &__buy-button {
      @apply w-full sm:w-auto;
    }
    &__container {
      @apply items-start flex-col sm:flex-row;
    }
    &__configurator-button {
      @apply flex w-full sm:w-auto items-center mt-2 sm:mt-0;
    }
    &__button-layout {
      @apply flex-col sm:flex-row items-start w-full mt-2 sm:mt-0;
    }
  }
}

.add-to-cart--PDP-stack-mobile {
  .layout {
    &__buy-button {
      @apply w-full sm:w-auto;
    }
    &__container {
      @apply items-start justify-start flex-col sm:flex-row;
    }
    &__configurator-button {
      @apply flex items-center;
    }
    &__button-layout {
      @apply w-full justify-start items-center mt-2 sm:mt-0;
    }
  }

  &.add-to-cart--quote-mode {
    .layout {
      &__container {
        @apply flex-col;
      }
      &__button-layout {
        @apply mt-2;
      }
    }
  }
}

.add-to-cart--variantTile {
  .layout {
    &__buy-button {
      @apply w-auto;
    }
    &__container {
      @apply justify-end;
    }
    &__configurator-button {
      @apply flex;
    }
  }
}
</style>
